/**
 * THE REGISTRY, AS A LOOKUP — so the board can be asked the one question it has
 * never been able to answer: WHICH BODIES HAVE NO CARD?
 *
 * ⚠️ WHY THIS FILE EXISTS. `GoverningBodies.jsx` derives its card list from the
 * five registry arrays and joins records ONTO those cards. The direction is
 * one-way: `championshipsFor(entity, universe)` walks the universe once per
 * entity, so a record whose `sr_conference` matches no entity is never pushed
 * into any card's rows and is counted nowhere. It is not filtered out — it never
 * enters the loop. That is why the Pac-12 could hold six championships assigned
 * to Philip and be, in the words of collegeAthleticBodies.js, "invisible from
 * every direction at once".
 *
 * Nothing in src/ compared the set of `sr_conference` values against the
 * registry. `tools/audit-map-coverage.mjs` does, from the command line, and its
 * 2026-09-30 run is the measurement this release is built on:
 *
 *     415 bodies with no card, 3,015 records behind them
 *      24 of those also carry a rep — the Pac-12 shape, repeated 24 times
 *
 * ⚠️ AND IT IS THE AUDIT'S INDEX, DELIBERATELY, NOT A SECOND ONE. The lookup
 * below is the same construction as audit-map-coverage.mjs lines 105-122: the
 * same five lists, in the same order, keyed on lowercased name AND acronym, with
 * the same `bareOf` fallback. A board that counted "no card" differently from the
 * audit would give two answers to one question, and the whole point of the number
 * is that somebody can check it. The order matters and is preserved: college is
 * added LAST there, so where an acronym collides the college class wins.
 *
 * READ ONLY. No I/O, no React. Built once at module load.
 */
import { STATE_BODIES, allEntities } from './stateAthleticBodies.js'
import { COLLEGE_BODIES } from './collegeAthleticBodies.js'
import { CANADA_BODIES, PRO_BODIES } from './otherAthleticBodies.js'
import { CHEER_BODIES } from './cheerAthleticBodies.js'
import { bareOf } from '../features/command-center/bodyMap.js'

const S = (v) => (v == null ? '' : String(v)).trim()
const L = (v) => S(v).toLowerCase()

/* lowercased name or acronym -> { cls, tab, card, key } */
const REG = new Map()

/* =========================================================================
 * 🔴 THE ACRONYM COLLISION — found 2026-10-14, measured and fixed 2026-10-15.
 *
 * `add()` below does `REG.set(L(e.acronym), rec)`, so WHERE TWO REGISTRY
 * ENTITIES SHARE AN ACRONYM THE LAST ONE WINS, SILENTLY. The registry holds
 * both sides of every collision, so nothing was missing — the index simply
 * threw one away:
 *
 *   MI-MHSAA / MS-MHSAA        -> MS wins (MS follows MI in the array)
 *   MA-MIAA  / MD-MIAA         -> MD wins
 *   WA-WIAA  / WI-WIAA         -> WI wins
 *   ID/IN/IA-IHSAA             -> IA wins
 *   CO-CHSAA / NY-CHSAA        -> NY wins
 *   AZ-CAA   / NCAA-CAA        -> NCAA wins (college is added LAST, by design)
 *
 * Then `registryCardFor('MHSAA (Michigan)')` missed on the exact string, fell
 * back to `bareOf` -> 'mhsaa', and got MISSISSIPPI. That is how 27 Michigan
 * championships came to display on Mississippi's card, and 115 Massachusetts
 * ones on Maryland's. The fallback was not reading a wrong value — it was
 * resolving a genuinely ambiguous one by coin flip.
 *
 * THE FIX IS DERIVED, NOT LISTED. Two structures, both built from the same
 * arrays, so neither can drift from the registry:
 *
 *  1. AMBIGUOUS — any lowercased token more than one DISTINCT entity claims.
 *     Asked for one of these, this module answers "no card" rather than
 *     guessing. That is the file's own stated principle applied to itself:
 *     a false MATCH hides a body that genuinely has no card, and hiding one
 *     is the failure this whole surface exists to end.
 *
 *  2. QUALIFIED — the aliases the OBJECT actually stores, so the ambiguity is
 *     resolvable instead of merely refused. `governing_body` on 2-64425303
 *     qualifies a body three ways, measured over 15,854 records:
 *       by jurisdiction  "MHSAA (Michigan)"  "CHSAA (New York)"
 *       by sector        "MIAA (public)"     "CAA (private/independent)"
 *       by affiliation   "ACAA (Christian)"
 *     Each is registered against the entity it truly names, so the card is
 *     reached BY NAME instead of by stripping the qualifier off and hoping.
 *
 * ⚠️ REG ITSELF IS UNCHANGED, DELIBERATELY. `tools/audit-map-coverage.mjs`
 * re-implements this same index and the two must give one answer, so the
 * construction, the order and the college-wins-on-collision behaviour all
 * stay exactly as they were. The new behaviour lives in registryCardFor().
 * The audit now imports that function rather than keeping its own copy.
 * ========================================================================= */

/* =========================================================================
 * ⚠️ TWO ACRONYMS CAN COLLIDE FOR TWO COMPLETELY DIFFERENT REASONS, AND
 * TREATING THEM ALIKE BREAKS 169 RECORDS THAT WERE NEVER BROKEN.
 *
 *  (A) A GENUINE COLLISION — different organisations, same letters:
 *        KCAA   Kansas Christian AA        vs  Kentucky Christian AA
 *        IACS   Illinois Assn of Christian Schools vs Indiana's
 *        MCAC   Minnesota Christian Conf   vs  Manitoba Colleges Conf
 *        ACAA   Alabama Christian AA       vs  Atlantic Collegiate AA
 *        MHSAA  Michigan / Mississippi / Manitoba
 *      There is no single right card, so the honest answer is none.
 *
 *  (B) A REGISTRY DUPLICATION — ONE organisation holding one row per state it
 *      operates in, because it is multi-state by nature:
 *        NEPSAC  New England Preparatory School Athletic Council   MA/NH/RI
 *        MAIS    Mid-South Association of Independent Schools      LA/MS
 *        HCAA    Heartland Christian Athletic Association          AR/OK
 *        ISL     Independent School League                         DC/MA
 *      These are the bodies research-rules calls out as multi-state. There IS
 *      a single right card, so refusing them would strip 92 MAIS + 62 NEPSAC
 *      + 15 HCAA records of a card they legitimately have.
 *
 * THE DISCRIMINATOR IS THE NAME, and it is read off the registry rather than
 * listed: claimants that share one `name` are one organisation. Only claimants
 * with DIFFERENT names make a token ambiguous.
 * ========================================================================= */

/** lowercased token -> Map of lowercased entity NAME -> first key claiming it. */
const CLAIMS = new Map()
/** lowercased qualified alias -> { cls, tab, card, key } */
const QUALIFIED = new Map()

const claim = (token, e) => {
  const t = L(token)
  if (!t) return
  if (!CLAIMS.has(t)) CLAIMS.set(t, new Map())
  const m = CLAIMS.get(t)
  const nm = L(e.name) || L(e.acronym)
  if (!m.has(nm)) m.set(nm, e.key || e.name || e.acronym)
}

const add = (e, cls, tab) => {
  const rec = { cls, tab, card: e.acronym || e.name, key: e.key || '' }
  if (e.name) REG.set(L(e.name), rec)
  if (e.acronym) REG.set(L(e.acronym), rec)
  /* Track the claim separately so the overwrite above stays visible. */
  if (e.acronym) claim(e.acronym, e)
}

/**
 * Register the qualified forms the object stores for this entity.
 *
 * ⚠️ AN ALIAS NEVER OVERWRITES A DIFFERENT ENTITY'S ALIAS. If two entities
 * would claim the same qualified string, that string is ambiguous too and
 * gets refused rather than resolved — the same rule as the bare acronym, for
 * the same reason.
 */
const addQualified = (e, cls, tab, jurisdiction) => {
  if (!e.acronym) return
  const rec = { cls, tab, card: e.acronym || e.name, key: e.key || '' }
  const forms = []
  /* by jurisdiction — only a real state/province, never an org code like
   * `st: 'NCAA'`, which is how the college array carries its parent body. */
  if (jurisdiction) forms.push(`${e.acronym} (${jurisdiction})`)
  /* by sector / affiliation — the qualifier vocabulary on the object */
  if (e.primary) forms.push(`${e.acronym} (public)`)
  else if (e.kind === 'independent' || e.kind === 'private') forms.push(`${e.acronym} (private/independent)`)
  else if (e.kind === 'christian') forms.push(`${e.acronym} (Christian)`)
  for (const f of forms) {
    claim(f, e)
    const k = L(f)
    const prev = QUALIFIED.get(k)
    if (prev && prev.key !== rec.key) continue   /* contested: leave it to AMBIGUOUS */
    QUALIFIED.set(k, rec)
  }
}

for (const e of allEntities(STATE_BODIES)) {
  const cls = e.primary ? 'hs-public' : `hs-${S(e.kind) || 'independent'}`
  add(e, cls, 'hs')
  addQualified(e, cls, 'hs', e.state)
}
for (const e of allEntities(CANADA_BODIES)) {
  const cls = e.primary ? 'canada-assoc' : 'canada-conference'
  add(e, cls, 'canada')
  addQualified(e, cls, 'canada', e.state)
}
for (const e of allEntities(PRO_BODIES)) add(e, 'pro', 'pro')
for (const e of allEntities(CHEER_BODIES)) add(e, 'cheer', 'cheer')
for (const e of allEntities(COLLEGE_BODIES)) {
  const cls = e.primary || e.level === 'National' ? 'college-national' : `college-conf-d${S(e.division) || 'I'}`
  add(e, cls, 'college')
  /* ⚠️ NO JURISDICTION ALIAS FOR COLLEGE. `e.state` here is the parent body
   * ('NCAA', 'NAIA'), not a place, so `CAA (NCAA)` would be a string nothing
   * stores and a second name for a card that already has one. Coastal AA is
   * reached by its full name, which is what the object stores for it. */
  addQualified(e, cls, 'college', null)
}

/**
 * Tokens claimed by more than one DIFFERENTLY-NAMED organisation. Asked for
 * one of these, `registryCardFor` answers null — there is no ONE card, and
 * naming either would be a guess wearing the costume of an answer.
 *
 * ⚠️ Keyed on distinct NAME, not distinct row, so a multi-state body with a
 * row per state (NEPSAC, MAIS, HCAA, ISL) is NOT ambiguous. See the long note
 * above: that distinction is worth 169 records.
 */
export const AMBIGUOUS = new Set([...CLAIMS.entries()].filter(([, v]) => v.size > 1).map(([k]) => k))

/** Diagnostic: which organisations claim an ambiguous token. */
export const claimantsOf = (token) => [...(CLAIMS.get(L(token)) || new Map()).entries()]
  .map(([name, key]) => ({ name, key }))

/**
 * Strings that are fused ON THE OBJECT rather than in the registry: one stored
 * `governing_body` value whose records demonstrably span more than one body,
 * so no entity is the right answer for it however the registry is arranged.
 *
 * Measured over a full page-through of 2-64425303, 15,854 records, snapshot
 * 2026-10-15T23:48Z. This mirrors FUSED_QUALIFIED in the backend's
 * `assignment/body-key.js` — the backend refuses to ASSIGN on these, and this
 * refuses to CARD them, which are the two halves of the same fact.
 *
 * ⚠️ KEPT AFTER THE DATA IS REPAIRED. The 2026-10-15 splits moved all of these
 * onto real bodies and the keys now hold zero records; a loader or an import
 * can put any of the strings back at any time.
 */
export const FUSED_ON_OBJECT = new Set([
  'ihsaa (public)',            /* 207: Idaho 98 + Indiana 58 + Iowa 51   — split 2026-10-14 */
  'wiaa (public)',             /* 247: Washington 161 + Wisconsin 86     — split 2026-10-15 */
  'mhsaa (public)',            /* 270: Mississippi 161 + Michigan 109    — split 2026-10-15 */
  'caa (private/independent)', /*  50: Canyon AA 26 + Coastal AA 24      — split 2026-10-15 */
  'ccaa',                      /*  35: California 13 + Canada 11 + 11 unattributable stubs */
])

/**
 * How many entities the five registry arrays hold. The denominator of
 * "bodies with a card", stated rather than assumed.
 */
export const REGISTRY_ENTITY_COUNT =
  allEntities(STATE_BODIES).length + allEntities(COLLEGE_BODIES).length
  + allEntities(CANADA_BODIES).length + allEntities(PRO_BODIES).length
  + allEntities(CHEER_BODIES).length

/** Distinct keys the registry answers to — a body may be reachable by two. */
export const REGISTRY_KEY_COUNT = REG.size

/**
 * THE REGISTRY AS DATA, for the one question this index cannot answer on its
 * own: not "does this label reach a card" but "WHICH registry row is this label
 * a different spelling of". `derivedBodies.js` needs the rows to answer that,
 * and building a second copy of the five arrays there is how two modules come to
 * disagree about what the registry holds.
 *
 * ⚠️ `labels` IS THE EXACT KEY SET, SO IT ANSWERS A NARROWER QUESTION THAN
 * `registryCardFor()`. That function falls back to `bareOf()`, so a
 * qualifier-stripped label reaches a card; this set does not include it. The
 * difference is the measurement: 7 labels on 71 records are reachable ONLY
 * through the stripped form, and where the acronym is a known collider that
 * match is on the wrong card. Keeping both lets the surface say so.
 */
export const REGISTRY_ENTRIES = []
for (const [src, tab] of [[STATE_BODIES, 'hs'], [COLLEGE_BODIES, 'college'],
  [CANADA_BODIES, 'canada'], [PRO_BODIES, 'pro'], [CHEER_BODIES, 'cheer']]) {
  for (const e of allEntities(src)) {
    REGISTRY_ENTRIES.push({ name: S(e.name), acronym: S(e.acronym), tab, key: S(e.key) })
  }
}
export const REGISTRY_LABELS = new Set(
  REGISTRY_ENTRIES.flatMap((e) => [e.name, e.acronym]).filter(Boolean).map(L),
)

/**
 * The card a governing-body name reaches, or null if the board holds none.
 *
 * ⚠️ EXACT MATCH ON THE WHOLE STRING, never a substring. `matcherFor` in
 * bodyChampionships.js does the fuzzy work of joining records to a card and has
 * the scars to prove it (\bAAA\b matching "Class AAA Football", IHSAA standing
 * for three states). This asks a narrower question — does a registry entry carry
 * this exact name or acronym — and a narrow question is the right one here:
 * a false MATCH would hide a body that genuinely has no card, which is the
 * failure this whole surface exists to end.
 */
export function registryCardFor(name) {
  const n = L(name)
  if (!n) return null

  /* 1. FUSED ON THE OBJECT — one stored value, several real bodies. No entity
   *    is the right answer, so there is no card. Checked FIRST, because some
   *    of these strings would otherwise resolve: `CAA (private/independent)`
   *    matches Canyon AA's sector alias exactly, and answering Canyon would
   *    quietly put a Division I college conference on an Arizona high-school
   *    card. */
  if (FUSED_ON_OBJECT.has(n)) return null

  /* 2. AMBIGUOUS — several entities claim this exact token. A bare `MHSAA` is
   *    Michigan, Mississippi AND Manitoba; the old code returned whichever was
   *    added last. "I cannot tell which" and "it is Mississippi" are different
   *    findings and reporting the first as the second is how 27 championships
   *    ended up on the wrong card. */
  if (AMBIGUOUS.has(n)) return null

  /* 3. The exact name or acronym, unchanged. */
  const exact = REG.get(n)
  if (exact) return exact

  /* 4. The qualified forms the object actually stores — the step that makes
   *    the refusals above cost nothing. `MHSAA (Michigan)` reaches Michigan,
   *    `MIAA (public)` reaches Massachusetts, `CHSAA (New York)` reaches the
   *    NY Catholic association. */
  const q = QUALIFIED.get(n)
  if (q) return q

  /* 5. Only now the bare fallback, and ONLY when the bare token is
   *    unambiguous. This is the line that caused the defect: it used to run
   *    unconditionally. A qualifier the registry has never heard of on an
   *    acronym only one body owns is still safely resolvable — that is the
   *    case this keeps working. */
  const bare = L(bareOf(name))
  if (!bare || bare === n) return null
  if (FUSED_ON_OBJECT.has(bare) || AMBIGUOUS.has(bare)) return null
  return REG.get(bare) || null
}

/** True when the board can open this body. */
export const hasCard = (name) => registryCardFor(name) != null
